<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useMusicPlayerStoreHook } from '@/store/modules/musicPlayer'
// 导入图标资源
import lastOneMusicIcon from '@/assets/space/last-one-music.svg'
import stopIcon from '@/assets/space/stop.svg'
import broadcatsIcon from '@/assets/space/broadcats.svg'
import nextOneMusicIcon from '@/assets/space/next-one-music.svg'
import volumeIcon from '@/assets/space/volume.svg'
import volumeMuteIcon from '@/assets/space/volume-mute.svg'
import fullScreenIcon from '@/assets/space/full-screen.svg'
import exitFullScreenIcon from '@/assets/space/half-screen.svg'
import MusicList from './MusicList.vue'
import EditLyrics from './EditLyrics.vue'
import { reqUpdateMusicLyrics } from '@/api/music/musicApi'
import { message } from '@/utils'
import { Code } from '@/config/httpCode'
import { MusicOriginType } from '@/types/constantsEnum'
import { reqGetMusicDetail } from '@/api/music/musicApi'
import {
  IconparkClose,
  IconparkOffScreenTwo,
  IconparkFullScreenTwo,
  IconparkHamburgerButton,
  IconparkScale,
  IconparkOverallReduction,
} from '@/components/Icon/index'

// 使用 musicPlayerStore
const musicPlayerStore = useMusicPlayerStoreHook()
const isEditLyrics = ref(false)
// 音频播放相关
const audioPlayer = ref<HTMLAudioElement | null>(null)
const isFullScreen = ref(false) // 单独维护全屏状态
const editLyricsText = ref('')
const isAudioLoading = ref(false)
const audioStatus = ref('')
const audioIsPayling = ref(false)

// 编辑歌词
const handleEditLyrics = () => {
  isEditLyrics.value = true
  editLyricsText.value = currentSongLyrics.value
}

const isCanEditLyrics = computed(() => {
  return (
    musicPlayerStore.originType === MusicOriginType.AiSelectCreatedMusic ||
    musicPlayerStore.originType === MusicOriginType.SpaceAiMusic ||
    musicPlayerStore.originType === MusicOriginType.SpaceMusicCreate ||
    musicPlayerStore.originType === MusicOriginType.SpaceMusicFavorite ||
    musicPlayerStore.originType === MusicOriginType.AiTaskMusic ||
    musicPlayerStore.originType === MusicOriginType.MusicTaskCreated
  )
})

// 保存歌词
const handleSaveLyrics = async () => {
  isEditLyrics.value = false
  currentSongLyrics.value = editLyricsText.value
  try {
    const res = await reqUpdateMusicLyrics(currentSong.value.musicId, {
      lyrics: editLyricsText.value,
    })
    if (res.statusCode === Code.SUCCESS.statusCode) {
      message.success('歌词更新成功')
      requestLyrics()
    } else {
      message.error('歌词更新失败')
    }
  } catch (error) {
    console.error('更新歌词失败:', error)
  }
}
// 计算属性：当前歌曲信息
const currentSong = computed(() => {
  if (musicPlayerStore.currentSongIndex !== null && musicPlayerStore.playlist.length > 0) {
    return musicPlayerStore.playlist[musicPlayerStore.currentSongIndex]
  }
  return null
})

const currentSongTitle = computed(() => {
  if (currentSong.value) {
    return currentSong.value.title
  }
  return 'aimusic灵镜'
})

const currentSongArtist = computed(() => {
  if (currentSong.value) {
    return currentSong.value?.voiceClone?.name || 'aimusic灵镜'
  }
  return 'aimusic灵镜'
})

/**
 * 处理歌词文本,移除标签格式
 */
const formatLyrics = (lyrics: string): string => {
  if (!lyrics) return ''

  try {
    // 首先确保lyrics是字符串
    if (typeof lyrics !== 'string') {
      return ''
    }

    // 处理换行符
    let processedLyrics = lyrics.replace(/\\n/g, '\n')

    // 移除歌词标签如[Verse]、[Chorus]等
    processedLyrics = processedLyrics.replace(/\[([\w\s\d\.]+)\]\n?/g, '')

    // 处理可能出现的多个连续换行
    processedLyrics = processedLyrics.replace(/\n{3,}/g, '\n\n')

    // 移除每行开头和结尾的空格
    processedLyrics = processedLyrics
      .split('\n')
      .map((line) => line.trim())
      .join('\n')

    // 确保不返回空文本
    if (!processedLyrics.trim()) {
      return ''
    }

    return processedLyrics
  } catch (error) {
    console.error('处理歌词文本时出错:', error)
    return ''
  }
}
const newLyrics = ref('')
// 计算属性：当前歌曲歌词 - 使用v-html渲染
const currentSongLyrics = computed(() => {
  if (!currentSong.value) return null
  // 兼容两种歌词属性名
  const lyricsText =
    newLyrics.value ||
    editLyricsText.value ||
    currentSong.value.lyrics ||
    currentSong.value.lycris ||
    null
  return formatLyrics(lyricsText)
})

/**
 * 格式化时间为 MM:SS 格式
 */
const formatTime = (seconds: number): string => {
  // 如果seconds不是有效数字或为0，则显示0:00
  if (!seconds || isNaN(seconds)) {
    return '0:00'
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
}

/**
 * 更新音频播放进度
 */
const updateProgress = () => {
  // 检查音频元素是否存在且当前是否有歌曲正在播放
  if (audioPlayer.value && musicPlayerStore.currentSongIndex !== null) {
    // 检查音频的当前时间和总时长是否有效
    const currentTime = audioPlayer.value.currentTime
    const duration = audioPlayer.value.duration || 0

    // 只有当时间值有效时才更新状态
    if (isFinite(currentTime) && isFinite(duration) && duration > 0) {
      musicPlayerStore.updateProgress(currentTime, duration)
    }
  }
}

/**
 * 通过点击进度条改变播放位置
 */
const seekMusic = (event: MouseEvent) => {
  if (audioPlayer.value && musicPlayerStore.duration > 0) {
    const progressBar = event.currentTarget as HTMLElement
    const clickPosition = event.offsetX
    const barWidth = progressBar.clientWidth
    const seekPercentage = clickPosition / barWidth
    const newTime = seekPercentage * musicPlayerStore.duration

    audioPlayer.value.currentTime = newTime
    musicPlayerStore.seekTo(newTime)
  }
}

/**
 * 关闭音乐播放
 */
const closePaly = () => {
  musicPlayerStore.togglePlay(false)
  musicPlayerStore.toggleFullscreen()
}

/**
 * 打开音乐列表
 */
const handleMusicDrawer = () => {
  musicPlayerStore.toggleDrawerList()
}

/**
 * 切换全屏状态
 */
const toggleFullScreen = () => {
  musicPlayerStore.toggleFullscreen()
  editLyricsText.value = ''
  // 如果开启全屏，禁用底层页面滚动
  document.body.style.overflow = musicPlayerStore.isFullscreen ? 'hidden' : ''
}

/**
 * 获取歌曲的音频URL
 */
const getAudioUrl = (song: any): string | undefined => {
  return song?.fileUrl || song.url
}

/**
 * 播放歌曲
 */
const playSong = async (index: number) => {
  try {
    musicPlayerStore.setCurrentSongIndex(index)
    musicPlayerStore.togglePlay()

    const audioElement = audioPlayer.value as HTMLAudioElement
    if (musicPlayerStore.playlist.length > 0 && musicPlayerStore.playlist[index]) {
      const song = musicPlayerStore.playlist[index]
      const audioUrl = getAudioUrl(song)

      if (!audioUrl) {
        console.error('歌曲URL不存在:', song)
        musicPlayerStore.togglePlay() // 切换回暂停状态
        return
      }

      // 在设置新的src之前，先暂停当前播放
      await audioElement.pause()

      // 设置新的音频源
      audioElement.src = audioUrl
      audioElement.volume = musicPlayerStore.volume / 100

      // 使用try-catch包裹play()
      try {
        await audioElement.play()
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('播放失败:', error)
          musicPlayerStore.togglePlay() // 切换回暂停状态
        }
      }
    }
  } catch (error) {
    console.error('播放过程出错:', error)
    musicPlayerStore.togglePlay() // 切换回暂停状态
  }
}

/**
 * 切换播放/暂停状态
 */
const togglePlay = async () => {
  if (!audioPlayer.value) return
  try {
    if (musicPlayerStore.currentSongIndex === null) {
      if (musicPlayerStore.playlist.length > 0) {
        await playSong(0)
      }
    } else if (audioPlayer.value.paused) {
      if (!audioPlayer.value.src) {
        const song = musicPlayerStore.playlist[musicPlayerStore.currentSongIndex]
        const audioUrl = getAudioUrl(song)

        if (!audioUrl) {
          console.error('歌曲URL不存在:', song)
          return
        }

        audioPlayer.value.src = audioUrl
      }

      try {
        await audioPlayer.value.play()
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('恢复播放失败:', error)
        }
      }
    } else {
      await audioPlayer.value.pause()
    }
  } catch (error) {
    console.error('切换播放状态失败:', error)
  }
}

/**
 * 播放下一首
 */
const playNext = async () => {
  musicPlayerStore.playNext()
}

/**
 * 播放上一首
 */
const playPrevious = async () => {
  musicPlayerStore.playPrevious()
}

/**
 * 音频播放结束处理
 */
const handleAudioEnded = async () => {
  try {
    await playNext()
  } catch (error) {
    // 忽略 AbortError
    if (error.name !== 'AbortError') {
      console.error('自动播放下一首失败:', error)
    }
  }
}

/**
 * 切换静音状态
 */
const toggleMute = () => {
  if (!audioPlayer.value) return

  musicPlayerStore.toggleMute()
  audioPlayer.value.volume = musicPlayerStore.isMuted ? 0 : musicPlayerStore.volume / 100
}

/**
 * 通过点击音量滑块改变音量
 */
const changeVolume = (event: MouseEvent) => {
  if (!audioPlayer.value) return

  const volumeBar = event.currentTarget as HTMLElement
  const clickPosition = volumeBar.clientHeight - event.offsetY
  const barHeight = volumeBar.clientHeight
  const newVolumeLevel = Math.max(0, Math.min(100, (clickPosition / barHeight) * 100))

  musicPlayerStore.setVolume(newVolumeLevel)
  audioPlayer.value.volume = newVolumeLevel / 100

  // 如果音量大于0，自动取消静音状态
  if (newVolumeLevel > 0 && musicPlayerStore.isMuted) {
    musicPlayerStore.toggleMute()
  }
}

/**
 * 处理音频错误
 */
const handleAudioError = (e) => {
  // 忽略 AbortError，因为这是正常的中断行为，不是真正的错误
  if (
    audioPlayer.value &&
    audioPlayer.value.error &&
    audioPlayer.value.error.name === 'AbortError'
  ) {
    return
  }

  // 尝试获取错误详情
  if (audioPlayer && audioPlayer.value) {
    const errorCode = audioPlayer.value.error ? audioPlayer.value.error.code : 'unknown'
    const errorMessage = audioPlayer.value.error ? audioPlayer.value.error.message : '未知错误'
    // console.error(`错误码: ${errorCode}, 错误信息: ${errorMessage}`)

    // 输出当前尝试播放的歌曲信息
    if (musicPlayerStore.currentSongIndex !== null && musicPlayerStore.playlist.length > 0) {
      const song = musicPlayerStore.playlist[musicPlayerStore.currentSongIndex]
      const audioUrl = getAudioUrl(song)
    }
  }
}
// 监听音频来源类型 判断是否请求歌词
watch(
  () => musicPlayerStore.isFullscreen,
  (newFullscreen) => {
    if (newFullscreen) {
      if (
        musicPlayerStore.originType === MusicOriginType.AiSelectCreatedMusic ||
        musicPlayerStore.originType === MusicOriginType.SpaceAiMusic ||
        musicPlayerStore.originType === MusicOriginType.SpaceMusicCreate ||
        musicPlayerStore.originType === MusicOriginType.SpaceMusicFavorite ||
        musicPlayerStore.originType === MusicOriginType.AiTaskMusic ||
        musicPlayerStore.originType === MusicOriginType.MusicTaskCreated
      ) {
        requestLyrics()
      }
    }
  },
)

/**
 * 请求歌词
 */
const requestLyrics = async () => {
  const res = await reqGetMusicDetail(currentSong.value.musicId)
  newLyrics.value = res?.data?.lycris || ''
}

// 监听currentSongIndex变化，更新音频源
watch(
  () => musicPlayerStore.currentSongIndex,
  (newIndex) => {
    if (newIndex !== null && musicPlayerStore.playlist.length > 0 && audioPlayer.value) {
      newLyrics.value = ''
      const song = musicPlayerStore.playlist[newIndex]
      // 获取正确的URL
      const audioUrl = getAudioUrl(song)

      audioPlayer.value.src = audioUrl || ''
      if (musicPlayerStore.isPlaying) {
        audioPlayer.value.play().catch((error) => {
          // console.error('播放失败:', error)
        })
      }
    }
  },
)
watch(
  () => musicPlayerStore.playlist,
  (newPlaylist) => {
    if (newPlaylist && newPlaylist.length > 0) {
      const url = newPlaylist[0].fileUrl || newPlaylist[0].url
      preloadDuration(url, 0)
    }
  },
)

/**
 * 预加载音频时长
 * @param url 音频URL
 * @param index 歌曲索引
 */
const preloadDuration = (url: string, index: number) => {
  const audio = new Audio()
  audio.src = url
  audio.addEventListener('loadedmetadata', () => {
    const song = musicPlayerStore.playlist[index]
    if (song) {
      //songs.value[index].duration = formatTimeToMMSS(audio.duration);
      song.duration = Math.floor(audio.duration).toString() + '秒'
      musicPlayerStore.setPlaylistDuration(index, song.duration)
      // 继续预加载下一首歌曲的时长
      if (index < musicPlayerStore.playlist.length - 1) {
        const nextSong = musicPlayerStore.playlist[index + 1]
        const nextUrl = nextSong.fileUrl || nextSong.url
        preloadDuration(nextUrl, index + 1)
      }
    }
  })

  // 添加错误处理
  audio.addEventListener('error', () => {
    console.error('无法加载音频:', url)
    // 即使加载失败，也尝试加载下一首
    if (index < musicPlayerStore.playlist.length - 1) {
      const nextSong = musicPlayerStore.playlist[index + 1]
      const nextUrl = nextSong.fileUrl || nextSong.url
      preloadDuration(nextUrl, index + 1)
    }
  })
}

// 监听isPlaying变化，控制播放/暂停
watch(
  () => musicPlayerStore.isPlaying,
  (newIsPlaying) => {
    if (audioPlayer.value) {
      if (newIsPlaying) {
        audioPlayer.value.play().catch((error) => {})
      } else {
        audioPlayer.value.pause()
      }
    }
  },
  { immediate: true },
)
// watch(
//   () => audioIsPayling.value,
//   (newIsPayling) => {
//     console.log('audioIsPayling===', newIsPayling)
//     if (newIsPayling) {
//       musicPlayerStore.playing()
//     } else {
//       musicPlayerStore.pause()
//     }
//     console.log('musicPlayerStore.isPlaying', musicPlayerStore.isPlaying)
//   },
// )
// 监听音量变化
watch(
  () => musicPlayerStore.volume,
  (newVolume) => {
    if (audioPlayer.value) {
      audioPlayer.value.volume = musicPlayerStore.isMuted ? 0 : newVolume / 100
    }
  },
)

// 监听静音状态变化
watch(
  () => musicPlayerStore.isMuted,
  (newIsMuted) => {
    if (audioPlayer.value) {
      audioPlayer.value.volume = newIsMuted ? 0 : musicPlayerStore.volume / 100
    }
  },
)

// 处理键盘事件
const handleKeyDown = (event: KeyboardEvent) => {
  // 如果当前焦点在输入框或文本区域，不处理空格事件
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
    return
  }

  // 按下Esc键退出全屏
  if (event.key === 'Escape' && musicPlayerStore.isFullscreen) {
    toggleFullScreen()
  }

  // 按下空格键控制播放/暂停
  if (event.code === 'Space' && musicPlayerStore.currentSongIndex !== null) {
    event.preventDefault() // 阻止页面滚动
    togglePlay()
  }
}

// 组件挂载时初始化
onMounted(() => {
  // 设置默认音量
  if (audioPlayer.value) {
    audioPlayer.value.volume = musicPlayerStore.volume / 100
  }

  // 添加键盘事件监听
  document.addEventListener('keydown', handleKeyDown)
})

// 清理资源方法
const clearMusic = () => {
  if (audioPlayer.value) {
    audioPlayer.value.pause()
    audioPlayer.value.src = ''
  }
}

const stopPlayer = () => {
  if (audioPlayer.value) {
    audioPlayer.value.pause()
    musicPlayerStore.togglePlay() // 更新store中的状态
  }
}

/**
 * 设置全屏状态
 */
const setFullscreen = (status: boolean) => {
  if (musicPlayerStore.isFullscreen !== status) {
    musicPlayerStore.toggleFullscreen()
    // musicPlayerStore.isFullscreen = status
    document.body.style.overflow = status ? 'hidden' : ''
  }
}

// 暴露方法给外部调用
defineExpose({
  clearMusic,
  stopPlayer,
  setFullscreen,
  toggleFullScreen,
})

// 组件卸载时清理资源
onUnmounted(() => {
  // 恢复页面滚动
  document.body.style.overflow = ''

  // 移除键盘事件监听
  document.removeEventListener('keydown', handleKeyDown)
})

// 添加对resetPlayer的监听
watch(
  () => musicPlayerStore.currentSongIndex,
  (newIndex) => {
    // 如果currentSongIndex被设置为null，表示播放器被重置
    if (newIndex === null && audioPlayer.value) {
      // 重置音频元素
      audioPlayer.value.pause()
      audioPlayer.value.currentTime = 0
      audioPlayer.value.src = ''

      // 阻止播放器发送timeupdate事件
      audioPlayer.value.removeEventListener('timeupdate', updateProgress)
      audioPlayer.value.removeEventListener('loadedmetadata', updateProgress)
      // 当播放被重置后，重新添加事件监听器以便将来播放使用
      setTimeout(() => {
        if (audioPlayer.value) {
          audioPlayer.value.addEventListener('timeupdate', updateProgress)
          audioPlayer.value.addEventListener('loadedmetadata', updateProgress)
        }
      }, 100)
    } else {
      if (audioPlayer.value) {
        const currentTime = audioPlayer.value.currentTime
        const duration = audioPlayer.value.duration || 0

        // 只有当时间值有效时才更新状态
        if (isFinite(currentTime) && isFinite(duration) && duration > 0) {
          musicPlayerStore.updateProgress(currentTime, duration)
        }
      }
    }
  },
  { immediate: true },
)

// 处理音频开始加载
const handleLoadStart = () => {
  isAudioLoading.value = true
  audioStatus.value = '加载中...'
}

// 处理音频可以播放
const handleCanPlay = () => {
  isAudioLoading.value = false
  audioStatus.value = '准备就绪'
}

// 处理音频缓冲中
const handleWaiting = () => {
  isAudioLoading.value = true
  audioStatus.value = '缓冲中...'
}

// 处理音频开始播放
const handlePlaying = () => {
  isAudioLoading.value = false
  audioStatus.value = '播放中'
  audioIsPayling.value = true
  musicPlayerStore.togglePlay(true)
}

// 处理音频暂停
const handlePause = () => {
  isAudioLoading.value = false
  audioStatus.value = '已暂停'
  audioIsPayling.value = false
  musicPlayerStore.togglePlay(false)
}
</script>
<template>
  <div :class="{ 'opacity-0': musicPlayerStore.playlist.length === 0 }">
    <audio
      ref="audioPlayer"
      @ended="handleAudioEnded"
      @timeupdate="updateProgress"
      @loadedmetadata="updateProgress"
      @error="handleAudioError"
      @loadstart="handleLoadStart"
      @canplay="handleCanPlay"
      @waiting="handleWaiting"
      @playing="handlePlaying"
      @pause="handlePause"
    ></audio>
    <transition name="player-bar-transition">
      <div>
        <!-- 播放控件 -->

        <div
          class="player-bar"
          v-show="
            (musicPlayerStore.isFullscreen && musicPlayerStore.currentSongIndex !== null) ||
            musicPlayerStore.isVisible
          "
        >
          <transition name="player-fade">
            <div class="player-bar__container">
              <div class="player-bar__controls">
                <img
                  :src="currentSong?.thumbnailUrl || currentSong?.imageUrl"
                  class="control-icon !w-[48px] !h-[48px] !mr-[12px] rounded-[5px] overflow-hidden"
                  @click="toggleFullScreen"
                  v-if="currentSong?.thumbnailUrl || currentSong?.imageUrl"
                />
                <img
                  :src="lastOneMusicIcon"
                  alt="上一首"
                  class="control-icon"
                  @click="playPrevious"
                />
                <div v-loading="isAudioLoading">
                  <img
                    v-if="musicPlayerStore.isPlaying"
                    :src="stopIcon"
                    alt="暂停"
                    class="control-icon control-icon--play"
                    @click="togglePlay"
                  />
                  <img
                    v-else
                    :src="broadcatsIcon"
                    alt="播放"
                    class="control-icon control-icon--play"
                    @click="togglePlay"
                  />
                </div>

                <img :src="nextOneMusicIcon" alt="下一首" class="control-icon" @click="playNext" />
              </div>

              <div class="player-bar__info flex items-center">
                <div class="current-song">
                  <div class="current-song__title">{{ currentSongTitle }}</div>
                  <div class="current-song__artist">{{ currentSongArtist }}</div>
                </div>
                <div class="flex items-center ml-[16px]" @click="handleMusicDrawer">
                  <HamburgerButton width="20px" height="20px" color="#fff" />
                </div>
              </div>

              <div class="player-bar__progress">
                <div class="music-progress" @click="seekMusic($event)">
                  <div class="progress-bar-container">
                    <div class="progress-bar-bg">
                      <div
                        class="progress-bar-fill"
                        :style="{ width: musicPlayerStore.audioProgress + '%' }"
                      ></div>
                    </div>
                  </div>
                  <div class="time-display">
                    <span>{{ formatTime(musicPlayerStore.currentTime) }}</span>
                    <span>{{ formatTime(musicPlayerStore.duration) }}</span>
                  </div>
                </div>
              </div>

              <div class="player-bar__volume">
                <div class="volume-control flex items-center">
                  <!-- <img
                    :src="musicPlayerStore.isMuted ? volumeMuteIcon : volumeIcon"
                    alt="音量"
                    class="volume-icon"
                    @click="toggleMute"
                  /> -->
                  <div class="volume-icon" @click="toggleMute">
                    <VolumeNotice width="20px" height="20px" color="#fff" />
                  </div>
                  <div class="volume-slider-container">
                    <div class="volume-slider-bg" @click="changeVolume($event)">
                      <div
                        class="volume-slider-fill"
                        :style="{ height: musicPlayerStore.volume + '%' }"
                      ></div>
                    </div>
                  </div>
                  <div class="ml-[24px]">
                    <!-- <img
                      :src="fullScreenIcon"
                      alt="全屏"
                      class="volume-icon !w-[16px] !h-[16px]"
                      @click="toggleFullScreen"
                      v-if="!musicPlayerStore.isFullscreen"
                    />
                    <img
                      :src="exitFullScreenIcon"
                      alt="退出全屏"
                      class="volume-icon !w-[20px] !h-[20px]"
                      @click="toggleFullScreen"
                      v-else
                    /> -->
                    <IconparkFullScreenTwo
                      @click="toggleFullScreen"
                      width="20px"
                      height="20px"
                      color="#fff"
                      v-if="!musicPlayerStore.isFullscreen"
                    />
                    <IconparkOverallReduction
                      @click="toggleFullScreen"
                      v-else
                      width="20px"
                      height="20px"
                      color="#ffffff"
                      stroke="#ffffff"
                    />
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
        <!-- 全屏歌词展示 -->
        <transition name="fullscreen-transition">
          <div
            class="fullscreen-container"
            :class="{ 'fullscreen-active': musicPlayerStore.isFullscreen }"
            v-show="musicPlayerStore.isFullscreen && musicPlayerStore.currentSongIndex !== null"
            @wheel.stop
            @touchmove.stop
            @click.stop
          >
            <!-- 退出全屏按钮 -->
            <div
              class="absolute top-[48px] right-[100px] z-[1000] flex items-center justify-center fullscreen-btn"
            >
              <el-tooltip content="退出全屏" placement="top" effect="dark">
                <!-- <img
                  :src="exitFullScreenIcon"
                  alt="退出全屏"
                  class="volume-icon !w-[24px] !h-[30px]"
                  @click="toggleFullScreen"
                /> -->
                <IconparkOverallReduction
                  width="20px"
                  height="20px"
                  color="#ffffff"
                  stroke="#ffffff"
                  @click="toggleFullScreen"
                />
              </el-tooltip>
            </div>
            <div
              class="absolute top-[48px] right-[48px] z-[1000] flex items-center justify-center fullscreen-btn"
            >
              <el-tooltip content="关闭" placement="top" effect="dark">
                <!-- <el-icon size="28px" class="close-icon" color="#999999"><Close /></el-icon> -->
                <IconparkClose width="20px" height="20px" color="#fff" @click="closePaly" />
              </el-tooltip>
            </div>
            <!-- 纯色背景层 -->
            <div class="fullscreen-overlay"></div>

            <!-- 模糊图片背景层 -->
            <div
              class="fullscreen-bg"
              :style="{
                backgroundImage: `url(${currentSong?.thumbnailUrl || currentSong?.imageUrl})`,
              }"
            ></div>

            <div class="max-w-maxWidth w-full h-full mx-auto flex relative z-10 fullscreen-content">
              <div class="flex flex-1 items-center justify-center h-full fullscreen-album">
                <div class="relative">
                  <img
                    src="@/assets/space/record.png"
                    alt="唱片"
                    class="w-[439px] h-[440px] record-disc"
                  />
                  <img
                    src="@/assets/space/levers.png"
                    class="absolute top-[-122px] left-[50%] translate-x-[-30%] w-[164px] h-[236px] record-arm"
                    :class="{ 'arm-paused': !musicPlayerStore.isPlaying }"
                  />
                  <div class="w-full h-full absolute top-0 left-0 flex items-center justify-center">
                    <img
                      :src="currentSong?.thumbnailUrl || currentSong?.imageUrl"
                      class="avatar w-[260px] h-[260px] rounded-[50%] overflow-hidden"
                      :class="{
                        rotating: musicPlayerStore.isPlaying,
                        paused: !musicPlayerStore.isPlaying,
                      }"
                    />
                  </div>
                </div>
              </div>
              <div class="flex flex-1 h-full items-center justify-center fullscreen-lyrics">
                <div class="h-[80%] w-full max-w-[500px] flex flex-col items-start justify-center">
                  <div class="text-[30px] font-bold text-[#F5F5F5] lyrics-title">
                    {{ currentSongTitle }}
                  </div>
                  <div
                    class="text-size14 font-[500] text-[#B3B3B3] mt-[6px] mb-[20px] lyrics-artist"
                  >
                    歌手：{{ currentSongArtist }}
                  </div>
                  <div
                    class="text-size14 text-[#FFFFFF] mt-[10px] mb-[20px]"
                    v-if="!currentSongLyrics && !isEditLyrics"
                  >
                    <span class="mr-[5px] text-size14">{{
                      isCanEditLyrics ? '还没有歌词' : '此歌曲没有歌词哦'
                    }}</span>
                    <span
                      class="text-themeColor text-size14 cursor-pointer hover-effect"
                      @click="handleEditLyrics"
                      v-if="isCanEditLyrics"
                      >点击编辑</span
                    >
                  </div>
                  <div class="w-full max-w-[500px] overflow-hidden lyrics-container">
                    <el-scrollbar height="500px" class="lyrics-scrollbar overflow-hidden relative">
                      <div
                        class="text-size14 text-[#FFFFFF] whitespace-pre-wrap leading-[1.8] py-[10px] text-start lyrics-content"
                        v-html="currentSongLyrics"
                        v-if="currentSongLyrics && !isEditLyrics"
                      ></div>
                      <EditLyrics v-model="editLyricsText" v-if="isEditLyrics" />
                    </el-scrollbar>
                  </div>
                  <div class="w-full mt-[10px] flex items-center justify-end" v-if="isEditLyrics">
                    <div
                      class="h-[28px] text-size12 text-[#AEAEB2] rounded-[50px] px-[23px] ml-[20px] hover-transition flex items-center justify-center border-[1px] border-[#AEAEB2] cursor-pointer"
                      @click="isEditLyrics = false"
                    >
                      取消
                    </div>
                    <div
                      class="h-[28px] text-size12 text-white rounded-[50px] px-[23px] ml-[20px] hover-transition flex items-center justify-center bg-home-aisound-btn cursor-pointer"
                      @click="handleSaveLyrics"
                    >
                      确定
                    </div>
                  </div>
                  <div
                    class="w-full flex items-center justify-end mt-[10px]"
                    v-if="!isEditLyrics && currentSongLyrics && isCanEditLyrics"
                  >
                    <span
                      class="text-themeColor cursor-pointer hover-effect text-size14"
                      @click="handleEditLyrics"
                      >编辑歌词</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
    <MusicList />
  </div>
</template>

<style lang="scss" scoped>
/* ===========================
 * 底部播放控制栏
 * =========================== */
.player-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  height: 80px;
  // background-color: rgba(0, 0, 0, 0.7);
  background-color: #000;
  width: 100%;
  z-index: 9999;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;

  &__container {
    max-width: 1000px;
    margin: 0 auto;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 15px;
    transition:
      opacity 0.3s ease-in-out,
      transform 0.3s ease-in-out;
  }

  &__info {
    // width: 180px;
    color: white;
    margin-left: 10px;
  }

  &__progress {
    flex: 1;
    padding: 0 20px;
  }

  &__volume {
    width: 120px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__controls {
    // width: 150px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

/* 当前播放歌曲信息 */
.current-song {
  &__title {
    font-size: 14px;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__artist {
    font-size: 12px;
    color: #999;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

/* 控制按钮 */
.control-icon {
  width: 30px;
  height: 30px;
  cursor: pointer;
  margin: 0 5px;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
    transform: scale(1.1);
  }

  &--play {
    width: 35px;
    height: 35px;
  }
}

/* ===========================
 * 音乐进度条样式
 * =========================== */
.music-progress {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.progress-bar-container {
  width: 100%;
  margin: 5px 0;
}

.progress-bar-bg {
  width: 100%;
  height: 4px;
  background-color: #444;
  border-radius: 2px;
  position: relative;
  cursor: pointer;

  &:hover {
    height: 6px;
  }
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #0770ed 0%, #3907ed 100%);
  border-radius: 2px;
  position: absolute;
  top: 0;
  left: 0;
  transition: width 0.1s linear;
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #909399;
  margin-top: 2px;
}

/* ===========================
 * 音量控制样式
 * =========================== */
.volume-control {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

.volume-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
    transform: scale(1.1);

    & + .volume-slider-container {
      display: flex;
    }
  }
}
.close-icon {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
    transform: scale(1.1);

    & + .volume-slider-container {
      display: flex;
    }
  }
}
.volume-slider-container {
  position: absolute;
  bottom: 25px;
  left: 10px;
  transform: translateX(-30%);
  height: 80px;
  width: 30px;
  background-color: #222;
  border-radius: 4px;
  padding: 8px 0;
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);

  &:hover {
    display: flex;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #222;
  }
}

.volume-slider-bg {
  width: 4px;
  height: 100%;
  background-color: #444;
  border-radius: 2px;
  position: relative;
  cursor: pointer;

  &:hover {
    width: 6px;
  }
}

.volume-slider-fill {
  width: 100%;
  background: linear-gradient(0deg, #0770ed 0%, #3907ed 100%);
  border-radius: 2px;
  position: absolute;
  bottom: 0;
  left: 0;
  transition: height 0.1s linear;
}

/* 唱片旋转动画 */
@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.record-disc {
  transition: transform 0.5s ease;
}

.rotating {
  animation: rotating 20s linear infinite;
}

.paused {
  animation-play-state: paused;
}

/* 让中央封面也跟随旋转 */
.avatar {
  transition: all 0.5s ease;
}

/* 播放器整体显示/隐藏过渡效果 */
.player-bar-transition-enter-active,
.player-bar-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.player-bar-transition-enter-from,
.player-bar-transition-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

/* 全屏模式切换动画 */
.fullscreen-transition-enter-active {
  animation: drawer-fade-in 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.fullscreen-transition-leave-active {
  animation: drawer-fade-out 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

@keyframes drawer-fade-in {
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes drawer-fade-out {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(100%);
  }
}

/* 播放器内容显示/隐藏过渡效果 */
.player-fade-enter-active,
.player-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.player-fade-enter-from,
.player-fade-leave-to {
  opacity: 0;
}

/* 全屏背景样式 */
.fullscreen-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999;
  overflow: hidden;
  background-color: #141414;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.fullscreen-content {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 10;
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.6s 0.1s cubic-bezier(0.23, 1, 0.32, 1);
}
.fullscreen-container.fullscreen-active .fullscreen-content {
  opacity: 1;
  transform: translateY(0);
}

/* 内容区域离场动画 */
.fullscreen-container:not(.fullscreen-active) .fullscreen-content {
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.record-arm {
  transform: translateY(30px);
  transform-origin: 20% 10%;
  transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s;
  opacity: 0;
}

/* 唱臂暂停状态 */
.arm-paused {
  transform: rotate(-30deg) !important;
  transition: transform 0.5s ease !important;
}

.fullscreen-container.fullscreen-active .record-arm {
  transform: translateY(0);
  opacity: 1;
}

/* 唱臂离场动画 */
.fullscreen-container:not(.fullscreen-active) .record-arm {
  transform: translateY(30px);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fullscreen-album,
.fullscreen-lyrics {
  flex: 1;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.fullscreen-album {
  opacity: 0;
  transform: translateY(60px);
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s;
  will-change: transform, opacity;
}

.fullscreen-container.fullscreen-active .fullscreen-album {
  opacity: 1;
  transform: translateY(0);
}

/* 唱片区域离场动画 */
.fullscreen-container:not(.fullscreen-active) .fullscreen-album {
  transform: translateY(60px);
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fullscreen-lyrics {
  opacity: 0;
  transform: translateY(70px);
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.3s;
  will-change: transform, opacity;
}

.fullscreen-container.fullscreen-active .fullscreen-lyrics {
  opacity: 1;
  transform: translateY(0);
}

/* 歌词区域离场动画 */
.fullscreen-container:not(.fullscreen-active) .fullscreen-lyrics {
  transform: translateY(70px);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fullscreen-btn {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.5s ease 0.4s;
}

.fullscreen-container.fullscreen-active .fullscreen-btn {
  opacity: 1;
  transform: translateY(0);
}

.fullscreen-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 1);
  z-index: 3;
  opacity: 0.7;
}

.fullscreen-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  filter: blur(30px);
  transform: scale(1.2);
  z-index: 2;
  transition: all 0.8s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
  }
}

.lyrics-title,
.lyrics-artist,
.lyrics-container {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.lyrics-title {
  transition-delay: 0.4s;
}

.lyrics-artist {
  transition-delay: 0.5s;
}

.lyrics-container {
  transition-delay: 0.6s;
}

.fullscreen-container.fullscreen-active .lyrics-title,
.fullscreen-container.fullscreen-active .lyrics-artist,
.fullscreen-container.fullscreen-active .lyrics-container {
  opacity: 1;
  transform: translateY(0);
}

/* 歌词元素离场动画 */
.fullscreen-container:not(.fullscreen-active) .lyrics-title {
  transform: translateY(30px);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fullscreen-container:not(.fullscreen-active) .lyrics-artist {
  transform: translateY(25px);
  opacity: 0;
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s;
}

.fullscreen-container:not(.fullscreen-active) .lyrics-container {
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s;
}

.lyrics-scrollbar {
  position: relative;
  :deep(.el-scrollbar__wrap) {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
    overscroll-behavior: contain;
    isolation: isolate; /* 创建新的堆叠上下文 */
  }

  :deep(.el-scrollbar__bar) {
    background-color: rgba(255, 255, 255, 0.1);
    opacity: 0.8;
  }
}

/* 歌词滚动区域渐变效果 */
.lyrics-scrollbar :deep(.el-scrollbar__bar) {
  opacity: 0.3;
}

.gradient-top {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.6) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  z-index: 2;
}

.gradient-bottom {
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.6) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  z-index: 2;
}

/* 歌词样式 */
.lyrics-content {
  /* 歌词标签样式 */
  :deep(.lyrics-tag) {
    display: block;
    color: #88a0ff;
    font-weight: 500;
    margin-top: 16px;
    margin-bottom: 8px;
    font-size: 13px;
    letter-spacing: 0.5px;
    opacity: 0.9;
    background: linear-gradient(90deg, rgba(136, 160, 255, 0.15) 0%, transparent 100%);
    padding: 2px 8px;
    border-radius: 4px;
    width: fit-content;
  }
}

/* 鼠标经过效果 */
.hover-effect {
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    text-decoration: underline;
    opacity: 0.85;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.loading-dot {
  width: 4px;
  height: 4px;
  background-color: #666;
  border-radius: 50%;
  animation: loading 1s infinite;
}

@keyframes loading {
  0% {
    opacity: 0.2;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.2;
  }
}

/* 加载动画样式 */
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
:deep(.el-loading-mask) {
  background-color: rgba(0, 0, 0, 0.5) !important;
}
</style>
